import { Component } from '@theme/component';

/**
 * @typedef {Object} ThccShareRefs
 * @property {HTMLButtonElement} [boton]
 * @property {HTMLElement} [aviso] Live region (role="status") owned by this component.
 * @property {HTMLDialogElement} [hoja] Mobile share sheet (native dialog).
 * @property {HTMLElement} [estado] Live region inside the share sheet.
 */

/**
 * Round share button. On mobile (< 750px) it opens the "Compartir producto" sheet (WhatsApp, copy link,
 * message, more). On desktop it uses the Web Share API when available; otherwise it copies the
 * link to the clipboard and announces the result in its own live region.
 *
 * Reads `data-title`, `data-url`, `data-msg-copied` and `data-msg-error`.
 * @extends {Component<ThccShareRefs>}
 */
class ThccShareButton extends Component {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;

  connectedCallback() {
    // Re-parenting re-runs connectedCallback with the new parent, which then initialises normally.
    if (this.#moveToGallery()) return;
    super.connectedCallback();
    this.#wireSheet();
  }

  /** Wires the share sheet controls (plain listeners: the sheet is a native <dialog>). */
  #wireSheet() {
    const { hoja } = this.refs;
    if (!hoja || hoja.dataset.cableada === 'true') return;
    hoja.dataset.cableada = 'true';

    hoja.addEventListener('click', (event) => {
      const target = event.target;
      if (target === hoja) {
        hoja.close();
        return;
      }
      const button = target instanceof Element ? target.closest('button[data-accion]') : null;
      if (!(button instanceof HTMLElement)) return;
      this.#accion(button.dataset.accion ?? '');
    });
  }

  /**
   * @param {string} accion
   */
  async #accion(accion) {
    const url = this.#safeUrl();
    if (!url) return;
    const title = this.dataset.title ?? '';
    const text = `${title} ${url}`;

    if (accion === 'whatsapp') {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
    } else if (accion === 'mensaje') {
      window.location.href = `sms:?&body=${encodeURIComponent(text)}`;
    } else if (accion === 'mas') {
      if (typeof navigator.share === 'function') {
        try {
          await navigator.share({ title, url });
        } catch {
          // The user closed the native sheet: nothing to do.
        }
      } else {
        await this.#copy(url, true);
      }
    } else if (accion === 'copiar') {
      await this.#copy(url, true);
    }
  }

  /**
   * @param {string} url
   * @param {boolean} inSheet
   */
  async #copy(url, inSheet) {
    try {
      await navigator.clipboard.writeText(url);
      this.#announce(this.dataset.msgCopied ?? '', inSheet);
    } catch {
      this.#announce(this.dataset.msgError ?? '', inSheet);
    }
  }

  /**
   * Moves the overlay variant of the button next to the gallery (not inside `media-gallery`, which
   * Horizon morphs on variant change). Returns true when the node was moved.
   * @returns {boolean}
   */
  #moveToGallery() {
    if (!this.classList.contains('thcc-pdp-compartir--superpuesto')) return false;

    const host = this.closest('product-component')?.querySelector(':scope > .product-information__media');
    if (!(host instanceof HTMLElement) || this.parentElement === host) return false;

    host.append(this);
    return true;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#timer);
  }

  /**
   * Handles the click on the share button.
   */
  async compartir() {
    const title = this.dataset.title ?? '';
    const url = this.#safeUrl();
    if (!url) return;

    const { hoja } = this.refs;
    if (hoja && typeof hoja.showModal === 'function' && window.matchMedia('(max-width: 749px)').matches) {
      if (!hoja.open) hoja.showModal();
      return;
    }

    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title, url });
        return;
      } catch (error) {
        // The user closed the native sheet: nothing to do.
        if (error instanceof DOMException && error.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      this.#announce(this.dataset.msgCopied ?? '');
    } catch {
      this.#announce(this.dataset.msgError ?? '');
    }
  }

  /**
   * Only same-origin http(s) links are shared.
   * @returns {string}
   */
  #safeUrl() {
    try {
      const base = new URL(this.dataset.url ?? '', window.location.origin);
      if (base.protocol !== 'https:' && base.protocol !== 'http:') return '';
      // The page URL carries ?variant= for the visible presentation; use it when it is the same product.
      const current = new URL(window.location.href);
      if (current.origin === base.origin && current.pathname === base.pathname) {
        return `${current.origin}${current.pathname}${current.search}`;
      }
      return base.href;
    } catch {
      return '';
    }
  }

  /**
   * @param {string} message
   * @param {boolean} [inSheet]
   */
  #announce(message, inSheet = false) {
    const { aviso, estado } = this.refs;
    const target = inSheet && estado ? estado : aviso;
    if (!target) return;

    clearTimeout(this.#timer);
    target.textContent = message;
    this.#timer = setTimeout(() => {
      target.textContent = '';
    }, 3000);
  }
}

if (!customElements.get('thcc-share-button')) {
  customElements.define('thcc-share-button', ThccShareButton);
}
